<template>
  <dialog
    ref="dialog"
    class="max-h-[90dvh] w-[calc(100%-2rem)] max-w-[977px] overflow-y-auto bg-tw-cream p-6 text-left font-inter text-black backdrop:bg-black/80 md:px-[67px] md:py-[60px]"
    aria-labelledby="consent-heading"
    aria-describedby="consent-description"
    @cancel="onCancel"
  >
    <div class="flex justify-end">
      <button
        type="button"
        class="text-lg underline underline-offset-4 hover:text-tw-rust focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tw-rust md:text-[22px]"
        :lang="isNorwegian ? 'en' : 'nb'"
        @click="common.actions.setLanguageCode()"
      >
        {{ t('otherLanguage') }}
      </button>
    </div>

    <template v-if="!state.showDetails">
      <h2 id="consent-heading" class="mt-2 text-2xl tracking-[-0.02em] md:text-[35px] md:leading-tight">{{ t('heading') }}</h2>
      <p id="consent-description" class="mt-4 text-lg font-light leading-8 tracking-[0.02em] md:text-[25px]">{{ t('intro') }}</p>
    </template>

    <template v-else>
      <h2
        id="consent-heading"
        ref="settingsHeading"
        tabindex="-1"
        class="mt-2 text-2xl tracking-[-0.02em] focus:outline-none md:text-[35px] md:leading-tight"
      >
        {{ t('settingsHeading') }}
      </h2>
      <p id="consent-description" class="mt-4 text-lg font-light leading-8 tracking-[0.02em] md:text-[25px]">
        {{ t('settingsIntro') }}
        <router-link
          to="/personvern"
          class="underline underline-offset-4 hover:text-tw-rust focus-visible:outline focus-visible:outline-2 focus-visible:outline-tw-rust"
          @click="close()"
        >
          {{ t('privacyLink') }}</router-link
        >.
      </p>

      <section class="mt-10 bg-[#e5e5e5] p-6 md:mt-20 md:px-[42px] md:py-[55px]" aria-labelledby="statistics-heading">
        <h3 id="statistics-heading" class="text-2xl tracking-[-0.02em] md:text-[30px]">{{ t('statisticsHeading') }}</h3>
        <p class="mt-4 text-lg font-light leading-8 tracking-[0.02em] md:text-[25px]">{{ t('statisticsText') }}</p>

        <label
          class="mt-6 inline-flex cursor-pointer items-center gap-3 border border-black/40 bg-tw-cream/40 px-4 py-2 text-lg shadow-[0_4px_4px_rgba(0,0,0,0.25)] md:text-[22px]"
        >
          <input
            v-model="state.statisticsChecked"
            type="checkbox"
            class="h-6 w-6 accent-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tw-rust"
          />
          {{ t('statisticsCheckbox') }}
        </label>

        <details class="group mt-6 border-t border-black/40 pt-6">
          <summary
            class="flex cursor-pointer list-none items-center justify-between gap-6 text-lg font-medium tracking-[0.02em] text-[#141414] focus-visible:outline focus-visible:outline-2 focus-visible:outline-tw-rust md:text-xl [&::-webkit-details-marker]:hidden"
          >
            {{ t('cookieTableHeading') }}
            <svg
              class="h-6 w-6 shrink-0 transition-transform group-open:rotate-45 motion-reduce:transition-none"
              viewBox="0 0 25 25"
              aria-hidden="true"
            >
              <path d="M0 10h25v5H0zM10 0h5v25h-5z" fill="currentColor" />
            </svg>
          </summary>
          <div class="mt-6 overflow-x-auto">
            <table class="w-full max-w-[651px] border border-black/60 text-left text-sm md:text-base">
              <caption class="sr-only">
                {{
                  t('cookieTableHeading')
                }}
              </caption>
              <thead>
                <tr class="border-b border-black/60">
                  <th scope="col" class="border-r border-black/60 px-5 py-4 font-semibold">{{ t('name') }}</th>
                  <th scope="col" class="px-5 py-4 font-semibold">{{ t('description') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="cookie in cookies" :key="cookie.name" class="border-b border-black/30 last:border-b-0">
                  <th scope="row" class="border-r border-black/60 px-5 py-3 font-normal">{{ cookie.name }}</th>
                  <td class="px-5 py-3">{{ t(cookie.description) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </details>
      </section>
    </template>

    <div class="mt-8 flex flex-wrap gap-4 md:mt-11">
      <button type="button" :class="choiceButtonClass" @click="choose(state.showDetails ? state.statisticsChecked : true)">
        {{ state.showDetails ? t('save') : t('accept') }}
      </button>
      <button type="button" :class="choiceButtonClass" @click="choose(false)">
        {{ t('decline') }}
      </button>
    </div>

    <button
      v-if="!state.showDetails"
      type="button"
      class="mt-12 text-lg underline underline-offset-4 hover:text-tw-rust focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tw-rust md:text-xl"
      @click="showDetails()"
    >
      {{ t('details') }}
    </button>
  </dialog>
</template>

<script setup lang="ts">
  import { computed, nextTick, onMounted, reactive, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  import useCommonStore from '@/composables/common'
  import useConsentStore from '@/composables/consent'

  const common = useCommonStore()
  const consent = useConsentStore()

  const dialog = ref<HTMLDialogElement | null>(null)
  const settingsHeading = ref<HTMLHeadingElement | null>(null)

  const state = reactive({
    showDetails: false,
    statisticsChecked: consent.getters.statistics.value === true,
  })

  const isNorwegian = computed(() => common.getters.languageCode.value.includes('no'))

  const choiceButtonClass =
    'h-[51px] w-full bg-[#1e1e1e] px-6 text-lg text-white hover:bg-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tw-rust sm:w-[196px] md:text-[22px]'

  const cookies = [
    { name: 'uio_ga', description: 'gaDescription' },
    { name: 'uio_ga_*', description: 'gaPrefixDescription' },
  ]

  onMounted(() => dialog.value?.showModal())

  // A choice is required the first time; afterwards Escape simply closes the dialog
  function onCancel(event: Event) {
    if (!consent.getters.hasChosen.value) event.preventDefault()
    else close()
  }

  // The details button disappears when the view switches, so keep focus inside the dialog
  async function showDetails() {
    state.showDetails = true
    await nextTick()
    settingsHeading.value?.focus()
  }

  function choose(statistics: boolean) {
    consent.actions.setStatistics(statistics)
    close()
  }

  function close() {
    dialog.value?.close()
    consent.actions.closeDialog()
  }

  const { t } = useI18n({
    messages: {
      no: {
        otherLanguage: 'English',
        heading: 'Dette nettstedet bruker informasjonskapsler',
        intro:
          'Noen informasjonskapsler er nødvendige for at nettsidene skal fungere, og du kan ikke velge dem bort. Andre informasjonskapsler er nødvendige for å samle statistikk, slik at vi kan forbedre og videreutvikle nettstedet. Du kan selv velge om du vil godta disse. Du kan trekke tilbake samtykke når som helst.',
        settingsHeading: 'Innstillinger for informasjons­kapsler',
        settingsIntro:
          'Noen informasjonskapsler (cookies) er nødvendige for at nettsidene skal fungere, andre data er ønskelig slik at vi kan gjøre nettstedet vårt enda bedre for deg som bruker. Les mer om behandling av personvern i vår',
        privacyLink: 'personvernerklæring',
        statisticsHeading: 'Statistikk og analyse',
        statisticsText:
          'Vi jobber kontinuerlig med å forbedre og videreutvikle nettstedene for at du skal finne og forstå informasjonen du ser etter, og få gjort det du kom for. For å hente statistikk og analysere bruksmønsteret på nettsidene bruker vi Google analytics.',
        statisticsCheckbox: 'Informasjonskapsler for statistikk',
        cookieTableHeading: 'Oversikt over informasjonskapsler for statistikk',
        name: 'Navn',
        description: 'Beskrivelse',
        gaDescription: 'Brukes av Google Analytics for å skille mellom ulike brukere/nettlesere.',
        gaPrefixDescription: 'Brukes flere av disse for de ulike delene av UiOs nettsted',
        accept: 'Godta',
        save: 'Lagre valg',
        decline: 'Avslå',
        details: 'Detaljer for informasjonskapsler',
      },
      en: {
        otherLanguage: 'Norsk',
        heading: 'This website uses cookies',
        intro:
          'Some cookies are necessary for the website to work, and you cannot opt out of them. Other cookies are used to collect statistics so that we can improve and develop the website. You can choose whether to accept these. You can withdraw your consent at any time.',
        settingsHeading: 'Cookie settings',
        settingsIntro:
          'Some cookies are necessary for the website to work; other data helps us make the website even better for you. Read more about how we handle personal data in our',
        privacyLink: 'privacy statement',
        statisticsHeading: 'Statistics and analytics',
        statisticsText:
          'We continuously work to improve and develop our websites so that you can find and understand the information you are looking for and get done what you came to do. We use Google Analytics to collect statistics and analyse usage patterns on the website.',
        statisticsCheckbox: 'Statistics cookies',
        cookieTableHeading: 'Overview of statistics cookies',
        name: 'Name',
        description: 'Description',
        gaDescription: 'Used by Google Analytics to distinguish between users/browsers.',
        gaPrefixDescription: 'Several of these are used for the different parts of the UiO website',
        accept: 'Accept',
        save: 'Save choices',
        decline: 'Decline',
        details: 'Cookie details',
      },
    },
  })
</script>
